// アップロードされたファイルを DuckDB の仮想 FS に登録し、テーブルを作成する。
//
// 役割: GeoJSON / FlatGeobuf は ST_Read（GDAL）、GeoParquet は read_parquet で
//       読み込み、生のテーブルを作る。取込時の CRS 正規化（4326 統一・MakeValid・
//       geom 改名）は geo-normalize.js が後段で担う（責務分離）。
// 関係: db.js の query()/getDb() を使用。import_data ツールや手動取込 UI から呼ばれる。
//
// 流用元: reference/deckgl-duckdb-template/src/duckdb/loadFile.js
import { getDb, getConnection, query } from './db'
import { detectCsvGeometry } from './csv-geometry'
import { csvBufferToUtf8 } from './encoding'

// 文字列を SQL の安全な識別子に変換する（英数字とアンダースコアのみ）。
function sanitizeIdentifier(base) {
  let id = base
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/^_+|_+$/g, '')
  if (!id) id = 'layer'
  if (/^[0-9]/.test(id)) id = `t_${id}`
  return id
}

// 既存テーブルと重複しない一意なテーブル名を生成する。
export async function uniqueTableName(base) {
  const existing = new Set(
    (
      await query(
        "SELECT table_name FROM information_schema.tables WHERE table_schema='main';",
      )
    ).map((r) => r.table_name),
  )
  const name = sanitizeIdentifier(base)
  if (!existing.has(name)) return name
  let i = 2
  while (existing.has(`${name}_${i}`)) i += 1
  return `${name}_${i}`
}

// テーブルのジオメトリ列（型 GEOMETRY）を検出する。
// GeoParquet は CRS 付きで "GEOMETRY('OGC:CRS84')" のように報告されることがあるため、
// 完全一致ではなく前方一致で判定する。
export async function findGeometryColumn(table) {
  const cols = await query(`PRAGMA table_info('${table}');`)
  const geom = cols.find((c) => String(c.type).toUpperCase().startsWith('GEOMETRY'))
  return geom ? geom.name : null
}

// SQL 文字列リテラル用にシングルクオートをエスケープする。
function quote(name) {
  return `'${name.replace(/'/g, "''")}'`
}

// テーブルに GEOMETRY 列が無い場合、WKB(BLOB) 列を ST_GeomFromWKB で geom に変換する。
// GeoParquet は spatial ロード時に自動デコードされるが、未デコード（BLOB のまま）の保険。
// 戻り値: ジオメトリ列名（'geom' など）／変換不能なら null。
// GeoParquet取込で、メタデータがない場合のWKB変換にも使用する。
export async function ensureGeometryColumn(table) {
  const existing = await findGeometryColumn(table)
  if (existing) return existing

  const cols = await query(`PRAGMA table_info('${table}');`)
  const blobs = cols.filter((c) => String(c.type).toUpperCase() === 'BLOB')
  const blob = blobs.find((c) => /^(geometry|geom|wkb)$/i.test(c.name)) ?? blobs[0]
  if (!blob) return null // ジオメトリ列なし（呼び出し側でエラーにする）

  await query(
    `CREATE OR REPLACE TABLE "${table}" AS SELECT * EXCLUDE ("${blob.name}"), ` +
      `ST_GeomFromWKB("${blob.name}") AS geom FROM "${table}";`,
  )
  return 'geom'
}

// GeoParquet からテーブルを作成する。
// read_parquet は GeoParquet メタデータ付きなら geometry 列を GEOMETRY に自動デコードする。
// 自動デコードされない場合は ensureGeometryColumn が WKB を変換する。
async function createTableFromParquet(table, virtualName) {
  await query(
    `CREATE TABLE "${table}" AS SELECT * FROM read_parquet(${quote(virtualName)});`,
  )
  await ensureGeometryColumn(table)
}

// CSV からテーブルを作成し、列名から推定したジオメトリ（geom 列）を付与する。
// WKT 列があれば ST_GeomFromText、無ければ経度・緯度列から ST_Point。
// どちらも無ければテーブルを破棄してエラーにする。
async function createTableFromCsv(table, virtualName) {
  // ヘッダ・型は read_csv_auto に自動判定させる（バイト列は loadFile 側で UTF-8 へ正規化済み）。
  await query(`CREATE TABLE "${table}" AS SELECT * FROM read_csv_auto(${quote(virtualName)});`)

  const cols = await query(`PRAGMA table_info('${table}');`)
  const detected = detectCsvGeometry(cols.map((c) => c.name))

  if (detected.mode === 'wkt') {
    const w = detected.wktColumn
    await query(
      `CREATE OR REPLACE TABLE "${table}" AS SELECT * EXCLUDE ("${w}"), ` +
        `ST_GeomFromText("${w}") AS geom FROM "${table}";`,
    )
    return
  }

  if (detected.mode === 'lonlat') {
    const { lonColumn, latColumn } = detected
    // 経度・緯度（EPSG:4326 の lon/lat）から点ジオメトリを作る。
    await query(
      `CREATE OR REPLACE TABLE "${table}" AS SELECT *, ` +
        `ST_Point(CAST("${lonColumn}" AS DOUBLE), CAST("${latColumn}" AS DOUBLE)) AS geom ` +
        `FROM "${table}";`,
    )
    return
  }

  await query(`DROP TABLE IF EXISTS "${table}";`)
  throw new Error(
    'CSV からジオメトリを作成できません。WKT 列（wkt / geometry など）または ' +
      '経度・緯度列（lon・lat など）が必要です。',
  )
}

// ファイルを読み込んでテーブルを作成する。
// 戻り値: { name, geomColumn, featureCount }
export async function loadFile(file) {
  const db = await getDb()
  await getConnection() // Spatial ロード済みを保証

  const ext = file.name.split('.').pop().toLowerCase()
  const virtualName = `upload_${Date.now()}_${file.name}`
  let buffer = new Uint8Array(await file.arrayBuffer())
  // CSV は Shift-JIS を UTF-8 へ正規化（バイナリ形式の parquet/fgb は触らない）。
  if (ext === 'csv') buffer = csvBufferToUtf8(buffer)
  await db.registerFileBuffer(virtualName, buffer)

  const baseName = file.name.replace(/\.[^.]+$/, '')
  const table = await uniqueTableName(baseName)

  if (ext === 'parquet') {
    await createTableFromParquet(table, virtualName)
  } else if (ext === 'csv') {
    await createTableFromCsv(table, virtualName)
  } else {
    // .geojson / .json / .fgb など GDAL 対応形式
    await query(`CREATE TABLE "${table}" AS SELECT * FROM ST_Read(${quote(virtualName)});`)
  }

  const geomColumn = await findGeometryColumn(table)
  if (!geomColumn) {
    await query(`DROP TABLE IF EXISTS "${table}";`)
    throw new Error(`ジオメトリ列が見つかりませんでした: ${file.name}`)
  }

  const [{ cnt }] = await query(`SELECT count(*) AS cnt FROM "${table}";`)

  return { name: table, geomColumn, featureCount: Number(cnt) }
}

// 任意の SELECT 文の結果をテーブル化してレイヤー化できる形で返す。
// ジオメトリ列を含まない場合はエラーにする。
// 戻り値: { name, geomColumn, featureCount }
export async function loadSqlAsTable(sql, baseName = 'query') {
  await getConnection() // Spatial ロード済みを保証

  const trimmed = sql.trim().replace(/;\s*$/, '')
  if (!/^select|^with/i.test(trimmed)) {
    throw new Error('レイヤー化できるのは SELECT / WITH 文のみです')
  }

  const table = await uniqueTableName(baseName)
  await query(`CREATE TABLE "${table}" AS ${trimmed};`)

  const geomColumn = await findGeometryColumn(table)
  if (!geomColumn) {
    await query(`DROP TABLE IF EXISTS "${table}";`)
    throw new Error(
      '結果にジオメトリ列（GEOMETRY 型）が含まれていません。' +
        'レイヤー化するには geom 列を返す SELECT を実行してください（例: SELECT * FROM "<レイヤー名>"）。' +
        'テーブル一覧などのメタデータ照会はレイヤー化できません。',
    )
  }

  const [{ cnt }] = await query(`SELECT count(*) AS cnt FROM "${table}";`)
  return { name: table, geomColumn, featureCount: Number(cnt) }
}

// 非空間 CSV を「データテーブル」として素のまま取り込む（ジオメトリ検出も正規化もしない）。
// 位置情報を持たない属性 CSV（例: 市区町村コード別の統計）を、空間レイヤーと SQL JOIN する
// ための材料として読み込む用途。createTableFromCsv（geom 必須）とは別経路。
// エンコーディングは csvBufferToUtf8 で UTF-8 へ正規化（Shift-JIS 対応）。
// 戻り値: { name }
export async function loadCsvAsTable(file, baseName) {
  const db = await getDb()
  await getConnection()

  const virtualName = `upload_${Date.now()}_${file.name}`
  const buffer = csvBufferToUtf8(new Uint8Array(await file.arrayBuffer()))
  await db.registerFileBuffer(virtualName, buffer)

  const base = baseName ?? file.name.replace(/\.[^.]+$/, '')
  const table = await uniqueTableName(base)
  await query(`CREATE TABLE "${table}" AS SELECT * FROM read_csv_auto(${quote(virtualName)});`)
  return { name: table }
}

// テーブルを削除する（レイヤー削除時に使用）。
export async function dropTable(table) {
  await query(`DROP TABLE IF EXISTS "${table}";`)
}
